/**
 * Level of detail for the Knowledge Archive graph — what is drawn at a given
 * zoom, and where.
 *
 * The brief: a graph you can actually read. Far out, only the big groups and
 * each one's most important concepts resolve; zoom in and the rest fills in;
 * pan away and what you left goes quiet again.
 *
 * Three gates, all pure functions of numbers so they test without a canvas,
 * and the first two **monotonic in zoom** — zooming in only ever adds. That is
 * a hard requirement rather than a nicety: a node that vanishes as you zoom
 * towards it takes its edges with it, which is the most disorienting thing
 * this view can do.
 *
 * 1. **Cluster gate** (`clusterIsLegible`) — which groups are drawn, by their
 *    size on screen. This is the one that governs how much is on the canvas.
 * 2. **Rank gate** (`clusterBudget`) — how many nodes per cluster are drawn.
 *    This works *because* of how `clusterLayout` places them: offset from the
 *    cluster centre grows as `√rank`, so hiding high ranks hides the outside
 *    of each cluster and keeps the core. Zooming in therefore reveals each
 *    cluster outward from its most important concept, which is the effect
 *    without any extra bookkeeping.
 * 3. **Viewport gate** (`visibleRect`) — what is near enough to the camera to
 *    be worth painting. `force-graph` does no culling of its own: it calls
 *    `nodeCanvasObject` for every visible node on every painted frame, so this
 *    is a real saving, not just a visual one.
 *
 * Links have no zoom gate of their own any more. Three constants used to ration
 * them by zoom and by length; all three are gone, because an edge inside a
 * cluster is only drawn when both its endpoints survive the gates above — which
 * already requires the cluster to be legible and on screen. Zoom is the gate,
 * one level up. See `useGraphLod`.
 *
 * Deliberately NOT done by swapping the node array. A new `graphData` identity
 * makes the library re-index d3, re-register hit-test colours, re-heat the
 * simulation and possibly snap the camera — during the very gesture that
 * caused it. `nodeVisibility` / `linkVisibility` are registered
 * `triggerUpdate: false` and cost one redraw, which is why LOD is expressed as
 * predicates.
 */

/**
 * Concepts drawn per cluster at zoom 1.
 *
 * Generous, and it can afford to be: the budget saturates against the
 * cluster's own size, so a two-concept group shows two however large this is.
 * What keeps a fragmented archive readable is `MIN_CLUSTER_PX` — how many
 * GROUPS get drawn — not how deep into each one we go. That is the thing two
 * earlier versions got backwards.
 *
 * It was 88 with a floor of 6, tuned against a fixture of twelve clean
 * subjects. A live archive has ~485 groups, because a cluster is one
 * `domain_tags[0]` value and those are LLM keywords, so `clusters x floor`
 * admitted 2,900 nodes — more than the server sends, and the gate could not
 * hide anything at all.
 */
const NODES_PER_CLUSTER = 82;

/** Slightly super-linear, so descending reads as resolving detail. */
const BUDGET_EXPONENT = 1.2;

/**
 * Every cluster shows at least its most important concept. One, not six: with
 * hundreds of groups a floor of six was the entire bug above.
 */
export const MIN_NODES_PER_CLUSTER = 1;

/**
 * A cluster is drawn once its radius covers this many screen pixels.
 *
 * By SIZE rather than by rank, deliberately. A rank gate needs the clusters
 * sorted globally, so zooming into a corner of the archive would still draw
 * "the twenty biggest" from everywhere rather than what is in front of you.
 * On-screen size is local, needs no sort, and gives the behaviour directly: a
 * big group resolves from far out, a two-concept group only once you have
 * zoomed near enough that naming it is worth the pixels.
 *
 * 28px, measured rather than picked: on a real 980-concept archive it draws
 * ~98 of 485 groups at the opening zoom and all of them by 190%, where 12px
 * drew all 485 at once and 40px drew exactly one.
 */
export const MIN_CLUSTER_PX = 28;

/** At or above this zoom the gate is off and everything in view is drawn. */
export const FULL_DETAIL_ZOOM = 2;

export interface ViewportInput {
  /** Zoom scale. */
  k: number;
  /** Graph coordinate at the canvas centre — what `onZoom` reports. */
  cx: number;
  cy: number;
  /** Canvas size in CSS pixels. */
  width: number;
  height: number;
}

/**
 * Whether a cluster is big enough on screen to be worth drawing at all.
 * `radius` is in graph units, as `clusterLayout` reports it.
 */
export function clusterIsLegible(radius: number, k: number): boolean {
  if (!Number.isFinite(k) || k <= 0) return true;
  if (k >= FULL_DETAIL_ZOOM) return true;
  return radius * k >= MIN_CLUSTER_PX;
}

/**
 * How many of one cluster's concepts are drawn at zoom `k`.
 *
 * A pure function of the zoom, and that is the whole point: **zooming in must
 * never take a node away.** Two earlier versions did, and it is the worst
 * failure this gate has, because what vanishes is usually the node whose edge
 * you were following.
 *
 * - Dividing a frame budget between the clusters currently legible meant that
 *   crossing the zoom where 288 single-concept groups appear cut every other
 *   cluster's share from 3 to 1. The painted TOTAL rose, which is why a
 *   count-based check missed it, while specific nodes disappeared.
 * - Scaling by distance from the view centre switched on at zoom 1 and took
 *   492 painted nodes down to 295 in one wheel click.
 *
 * Both are gone, and with them `focusFalloff` and `focusRadius`. The budget
 * rises with zoom and nothing else touches it, so the visible set can only
 * grow. "Pan away and the region goes quiet" is still honoured — by the
 * viewport gate, which is where it always belonged.
 */
export function clusterBudget(k: number): number {
  if (!Number.isFinite(k) || k <= 0) return MIN_NODES_PER_CLUSTER;
  if (k >= FULL_DETAIL_ZOOM) return Number.POSITIVE_INFINITY;
  return Math.max(
    MIN_NODES_PER_CLUSTER,
    Math.round(NODES_PER_CLUSTER * Math.pow(k, BUDGET_EXPONENT))
  );
}

export interface GraphRect {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Generous on purpose. Culling exactly at the canvas edge makes nodes blink in
 * and out as you pan, which reads as a glitch; a margin means they are already
 * painted by the time they arrive.
 */
export const VIEWPORT_MARGIN_PX = 240;

/** The graph-space rectangle worth painting, in graph units. */
export function visibleRect(
  view: ViewportInput,
  marginPx: number = VIEWPORT_MARGIN_PX
): GraphRect {
  const k = view.k > 0 ? view.k : 1;
  const halfWidth = view.width / 2 / k + marginPx / k;
  const halfHeight = view.height / 2 / k + marginPx / k;
  return {
    minX: view.cx - halfWidth,
    maxX: view.cx + halfWidth,
    minY: view.cy - halfHeight,
    maxY: view.cy + halfHeight,
  };
}

export function isInRect(
  x: number | undefined,
  y: number | undefined,
  rect: GraphRect
): boolean {
  if (x == null || y == null) return false;
  return x >= rect.minX && x <= rect.maxX && y >= rect.minY && y <= rect.maxY;
}
